import React from 'react';
import { motion } from 'framer-motion';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import CompositeTrendChart from './CompositeTrendChart';
import CrossFunctionalHeatMap from './CrossFunctionalHeatMap';
import SankeyDiagram from './SankeyDiagram';
import ExecutiveAlertsPanel from './ExecutiveAlertsPanel';
import InsightsCards from './InsightsCards';
import RiskDashboard from './RiskDashboard';
import OpportunitiesPanel from './OpportunitiesPanel';
import PerformanceVsTargetsRadar from './PerformanceVsTargetsRadar';
import EfficiencyMarginCorrelation from './EfficiencyMarginCorrelation';
import TopRisksPanel from './TopRisksPanel';
import TopOpportunitiesPanel from './TopOpportunitiesPanel';
const ExecutiveSummaryDashboard: React.FC = () => {
  const executiveKPIs = [{
    title: 'Total Revenue',
    value: '₹9,250 Cr',
    change: '+5.2%',
    isPositive: true,
    trend: 'YoY Growth'
  }, {
    title: 'EBITDA %',
    value: '18.3%',
    change: '+0.8%',
    isPositive: true,
    trend: 'Margin Expansion'
  }, {
    title: 'Customer Concentration',
    value: '18.5%',
    change: '-1.2%',
    isPositive: true,
    trend: 'Diversification'
  }, {
    title: 'Warranty Cost',
    value: '₹185 Cr',
    change: '+2.1%',
    isPositive: false,
    trend: 'Quality Focus'
  }, {
    title: 'WC Cycle Days',
    value: '46',
    change: '-3 days',
    isPositive: true,
    trend: 'Efficiency Gain'
  }, {
    title: 'Supply Chain Health',
    value: '87/100',
    change: '+2 pts',
    isPositive: true,
    trend: 'Strong'
  }];
  // Cross-Functional KPI Bar Data
  const crossFunctionalKPIs = [{
    title: 'Revenue',
    value: '₹9,250 Cr',
    target: '₹9,000 Cr',
    achievement: 102.8,
    change: '+5.2%',
    isPositive: true,
    domain: 'Sales',
    color: 'from-blue-500 to-blue-600'
  }, {
    title: 'EBITDA %',
    value: '18.3%',
    target: '17.5%',
    achievement: 104.6,
    change: '+0.8%',
    isPositive: true,
    domain: 'Finance',
    color: 'from-green-500 to-green-600'
  }, {
    title: 'OEE %',
    value: '82.5%',
    target: '85.0%',
    achievement: 97.1,
    change: '+2.3%',
    isPositive: true,
    domain: 'Operations',
    color: 'from-purple-500 to-purple-600'
  }, {
    title: 'Warranty Cost',
    value: '₹185 Cr',
    target: '₹165 Cr',
    achievement: 89.2,
    change: '+2.1%',
    isPositive: false,
    domain: 'Quality',
    color: 'from-orange-500 to-orange-600'
  }, {
    title: 'Audit Score %',
    value: '94.2%',
    target: '95.0%',
    achievement: 99.2,
    change: '+1.5%',
    isPositive: true,
    domain: 'Compliance',
    color: 'from-indigo-500 to-indigo-600'
  }, {
    title: 'WC Days',
    value: '46',
    target: '45',
    achievement: 97.8,
    change: '-3 days',
    isPositive: true,
    domain: 'Finance',
    color: 'from-teal-500 to-teal-600'
  }];
  return <div data-id="element-2836">
      <DescriptionPanel title="Executive Command Center" description="Unified view of business performance across all functions with AI-powered insights, risk monitoring, and strategic opportunities. This dashboard synthesizes data from sales, marketing, after-sales, and finance to provide actionable intelligence for executive decision-making." insights={['Revenue momentum strong at 12.4% YoY with West region leading growth', 'EBITDA margin expansion of 80 bps driven by operational excellence', 'Material cost inflation poses ₹185 Cr risk - mitigation strategies active', 'Top 5 strategic opportunities identified worth ₹720 Cr+ in value']} data-id="element-2837" />
      {/* Executive Header */}
      <div className="mb-8" data-id="element-2838">
        <div className="flex items-center justify-between mb-2" data-id="element-2839">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-2840">
            Executive Summary
          </h1>
          <div className="text-sm text-gray-500" data-id="element-2841">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
          </div>
        </div>
      </div>
      {/* Cross-Functional KPI Bar */}
      <div className="mb-8" data-id="element-2842">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2843">
          Cross-Functional Performance Dashboard
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6" data-id="element-2844">
          {crossFunctionalKPIs.map((kpi, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} data-id="element-2845">
              <Card className={`p-6 bg-gradient-to-br ${kpi.color} text-white`} data-id="element-2846">
                <div className="flex flex-col h-full" data-id="element-2847">
                  <div className="text-xs font-bold uppercase tracking-wider mb-1 text-white/80" data-id="element-2848">
                    {kpi.domain}
                  </div>
                  <div className="text-xs font-semibold mb-3 text-white/90" data-id="element-2849">
                    {kpi.title}
                  </div>
                  <div className="text-3xl font-bold mb-2" data-id="element-2850">{kpi.value}</div>
                  <div className="text-xs mb-2 text-white/80" data-id="element-2851">
                    Target: {kpi.target}
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2852">
                    <div className={`text-sm font-bold flex items-center ${kpi.isPositive ? 'text-white' : 'text-red-200'}`} data-id="element-2853">
                      {kpi.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-2854" /> : <TrendingDownIcon size={14} className="mr-1" data-id="element-2855" />}
                      {kpi.change}
                    </div>
                    <div className={`text-xs font-bold px-2 py-1 rounded ${kpi.achievement >= 100 ? 'bg-white/20' : kpi.achievement >= 95 ? 'bg-yellow-400/30' : 'bg-red-400/30'}`} data-id="element-2856">
                      {kpi.achievement.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </div>
      </div>
      {/* Auto-generated Insights */}
      <InsightsCards data-id="element-2857" />
      {/* Executive Alerts Panel */}
      <ExecutiveAlertsPanel data-id="element-2858" />
      {/* Multi-Domain Visuals Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-2859">
        <PerformanceVsTargetsRadar data-id="element-2860" />
        <EfficiencyMarginCorrelation data-id="element-2861" />
      </div>
      {/* Composite Trend and Heat Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-2862">
        <CompositeTrendChart data-id="element-2863" />
        <CrossFunctionalHeatMap data-id="element-2864" />
      </div>
      {/* Sankey Diagram - Full Width */}
      <SankeyDiagram data-id="element-2865" />
      {/* Top 10 Risks and Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-2866">
        <TopRisksPanel data-id="element-2867" />
        <TopOpportunitiesPanel data-id="element-2868" />
      </div>
    </div>;
};
export default ExecutiveSummaryDashboard;